import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from 'prop-types';
import * as actions from "./../details/detailsAction";
import FlightArrivalIcon from "../../assets/images/Web_FlightDeparture_Icon.svg";
import { FaLocationDot } from "react-icons/fa6";

/**
 * Optimized PreviousTaskContainer functional component for displaying previous task information
 * @param {Object} props - Component props
 * @param {Object} props.data - Employee and task data
 * @param {Object} props.fetchDetailsParams - Parameters for fetching details
 * @param {Function} props.update - Update function from parent component
 * @returns {JSX.Element} Rendered previous task container
 */
const PreviousTaskContainer = memo(({ data, fetchDetailsParams, update }) => {
  const dispatch = useDispatch();
  
  // Redux state selectors with memoization
  const previousTasks = useSelector(state => state.detailsReducer.previousTasks);
  // Note: previousTasksArr available but not currently used in this component
  // const previousTasksArr = useSelector(state => state.detailsReducer.previousTasksArr);
  
  // Local state for component-specific previous tasks
  const [localPreviousTasks, setLocalPreviousTasks] = useState({});

  // Memoized fetch function to prevent recreation
  const fetchPreviousTasks = useCallback(() => {
    if (data?.employeeId && data?.taskId && fetchDetailsParams?.station && fetchDetailsParams?.company) {
      return dispatch(actions.fetchPreviousTasks(
        data.employeeId,
        data.taskId,
        fetchDetailsParams.station,
        fetchDetailsParams.company
      ));
    }
  }, [dispatch, data?.employeeId, data?.taskId, fetchDetailsParams?.station, fetchDetailsParams?.company]);

  // Effect for component mount - fetch previous tasks
  useEffect(() => {
    fetchPreviousTasks();
  }, [fetchPreviousTasks]);

  // Effect for handling previous tasks updates
  useEffect(() => {
    const updatePreviousTasks = async () => {
      if (update && previousTasks) {
        await update(previousTasks);
      }

      // Perform a deep comparison to avoid unnecessary updates
      const isPreviousTaskDifferent = JSON.stringify(data?.previousTask) !== JSON.stringify(localPreviousTasks);
      if (isPreviousTaskDifferent) {
        setLocalPreviousTasks(data?.previousTask || {});
      }
    };

    updatePreviousTasks();
  }, [previousTasks, data?.previousTask, update]);

  // Memoized container styles to prevent recreation
  const containerStyles = useMemo(() => ({
    background: "#e4e9ed",
    color: "#36495a",
    height: 150,
    padding: 20,
    margin: 5,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignContent: "center",
  }), []);

  const leftColumnStyles = useMemo(() => ({
    height: 80,
    width: 200,
    padding: 10,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
  }), []);

  const rightColumnStyles = useMemo(() => ({
    height: 80,
    width: 200,
    padding: 10,
    marginTop: 15,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  }), []);

  const taskRowStyles = useMemo(() => ({
    display: "flex",
    flexDirection: "row"
  }), []);

  const iconStyles = useMemo(() => ({
    height: 25,
    width: 25,
    marginLeft: 10
  }), []);

  const locationIconStyles = useMemo(() => ({
    paddingLeft: 5
  }), []);

  // Memoized safe data extraction
  const safeTaskData = useMemo(() => {
    const tasks = localPreviousTasks || {};
    return {
      userId: tasks.userId || "NA",
      shiftStartTime: tasks.shiftData?.shiftStartTime?.substr(10) || "NA",
      shiftEndTime: tasks.shiftData?.shiftEndTime?.substr(10) || "NA",
      taskDisplayName: tasks.taskDisplayName || "NA",
      taskLocation: tasks.taskLocation || "NA",
      startTime: tasks.startTime || "NA",
      endTime: tasks.endTime || "NA",
      hasValidShiftData: tasks.shiftData && 
                        tasks.shiftData.shiftStartTime && 
                        tasks.shiftData.shiftEndTime,
      hasValidTaskName: tasks.taskDisplayName !== null && 
                       tasks.taskDisplayName !== undefined,
      isValidTask: tasks !== undefined && tasks !== null
    };
  }, [localPreviousTasks]);

  return (
    <div 
      className="border border-dark border-1"
      style={containerStyles}
      role="region"
      aria-label="Previous task information"
    >
      {/* Left Column - Employee and Shift Information */}
      <div style={leftColumnStyles}>
        <div style={{ fontSize: 15 }}>
          EE # {safeTaskData.userId}
        </div>
        <div style={{ fontWeight: "bold", fontSize: 15, marginTop: 10 }}>
          Shift Time:
        </div>
        <div>
          {safeTaskData.shiftStartTime} - {safeTaskData.shiftEndTime}
        </div>
      </div>

      {/* Right Column - Previous Task Information */}
      <div style={rightColumnStyles}>
        <div style={{ fontWeight: "bold" }}>Previous Task</div>
        
        <div style={taskRowStyles}>
          <div>
            {safeTaskData.hasValidTaskName ? safeTaskData.taskDisplayName : "NA"}
          </div>
          <div>
            <img
              src={FlightArrivalIcon}
              alt="Flight departure icon"
              style={iconStyles}
            />
          </div>
        </div>

        <div>
          {safeTaskData.taskLocation}
          <FaLocationDot style={locationIconStyles} aria-label="Location" />
        </div>
        
        <div>
          {safeTaskData.startTime} - {safeTaskData.endTime}
        </div>
        
        <div style={{ fontWeight: "bold" }}>FINISHED</div>
      </div>
    </div>
  );
});

// Display name for debugging
PreviousTaskContainer.displayName = 'PreviousTaskContainer';

// PropTypes for type checking
PreviousTaskContainer.propTypes = {
  data: PropTypes.shape({
    employeeId: PropTypes.string,
    taskId: PropTypes.string,
    previousTask: PropTypes.object
  }),
  fetchDetailsParams: PropTypes.shape({
    station: PropTypes.string,
    company: PropTypes.string
  }),
  update: PropTypes.func
};

// Default props
PreviousTaskContainer.defaultProps = {
  data: {},
  fetchDetailsParams: {},
  update: null
};

export default PreviousTaskContainer;
